import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "react-hot-toast";
import { MdArrowBack } from "react-icons/md";
import axios from "axios";
import {
  GoogleMap,
  LoadScript,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";

import Loader from "./Loader";
import OrderInfo from "./OrderInfo";
import ShippingInfo from "./ShippingInfo";
import OrderItems from "./OrderItems";

import { setCenter } from "../../../redux/actions/mapActions";
import { server } from "../../../server";
import statusColors from "../../../utils/statusColor";
import orderStatusDelivery from "../../../utils/orderStatusUtility";

/**
 * Full OrderDetails component with:
 * - Original status change UI + logic
 * - Delivery-boy helper tools (navigation, live location, ETA/distance, WhatsApp, call)
 *
 * Requirements:
 * - REACT_APP_GOOGLE_MAPS_API_KEY in .env
 * - orderStatusDelivery utility present (same structure as your project)
 * - Redux state: order.orders, orderStatus.orderStatuses
 * - server constant for API base url
 *
 * Note: this component expects `order.user.phoneNumber` for call/whatsapp.
 */

const OrderDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderId } = useParams();

  let { orders } = useSelector((state) => state.order);
  const { orderStatuses } = useSelector((state) => state.orderStatus || {});
  const { center: reduxCenter } = useSelector((state) => state.map || {});

  const order = orders?.find((o) => o._id === orderId);

  // --- Status change states (restored from previous code) ---
  const [status, setStatus] = useState("");
  const [statusId, setStatusId] = useState("5");
  const [displayStatus, setDisplayStatus] = useState("");
  const [statusDisable, setStatusDisable] = useState([]);

  // --- Delivery / customer location ---
  const [deliveryLocation, setDeliveryLocation] = useState(null); // delivery boy
  const [userLocation, setUserLocation] = useState(null); // customer
  const [tripStarted, setTripStarted] = useState(false);

  // --- Directions, distance, ETA ---
  const [directions, setDirections] = useState(null);
  const [distance, setDistance] = useState(null);
  const [eta, setEta] = useState(null);

  // --- statusOptions derived from Redux orderStatuses (if present) ---
  const statusOptions = orderStatuses
    ? orderStatuses.map(({ status: st, statusId: sid }) => {
        const display = orderStatusDelivery?.[sid]
          ? orderStatusDelivery[sid][0]
          : st;
        return { status: st, statusId: sid, displayStatus: display };
      })
    : [];

  // ------------------------------
  // Initialize status fields when order loads
  // ------------------------------
  useEffect(() => {
    if (!order) return;

    setStatus(order.status || "");
    setStatusId(order?.statusId ?? "5");

    // If orderStatusDelivery is in the project, set displayStatus if available
    const idx = order?.statusId;
    if (orderStatusDelivery && orderStatusDelivery[idx]) {
      setDisplayStatus(orderStatusDelivery[idx][0]);
    }

    // Also set user's location from order if available
    if (order.userCurrentLocation) {
      setUserLocation({
        lat: order.userCurrentLocation.latitude,
        lng: order.userCurrentLocation.longitude,
      });
    }
  }, [order]);

  // ------------------------------
  // Restore your previous mapping logic for statusDisable
  // ------------------------------
  useEffect(() => {
    if (!orderStatusDelivery) return;

    // mapping keys are the statusId tokens (like orderStatusDelivery[5][2]) as in your prior code
    const statusMapping = {
      [orderStatusDelivery[5][2]]: [5, 7, 8, 9, 10, 12, 13, 16, 17, 18],
      [orderStatusDelivery[6][2]]: [5, 6, 8, 9, 10, 12, 13, 16, 17, 18],
      [orderStatusDelivery[7][2]]: [5, 6, 7, 9, 10, 12, 13, 16, 17, 18],
      [orderStatusDelivery[8][2]]: [5, 6, 7, 8, 10, 12, 13, 16, 17, 18],
      [orderStatusDelivery[9][2]]: [5, 6, 7, 8, 9, 12, 13, 16, 17, 18],
      [orderStatusDelivery[10][2]]: [5, 6, 7, 8, 9, 10, 13, 16, 17, 18],
      [orderStatusDelivery[12][2]]: [5, 6, 7, 8, 9, 10, 12, 16, 17, 18],
      [orderStatusDelivery[13][2]]: [5, 6, 7, 8, 9, 10, 12, 13, 17, 18],
      [orderStatusDelivery[17][2]]: [5, 6, 7, 8, 9, 10, 12, 13, 16, 17],
      [orderStatusDelivery[18][2]]: [5, 6, 7, 8, 9, 10, 12, 13, 16, 17],
    };

    // statusId may be a string or token in your system; check mapping
    if (statusMapping[statusId]) {
      const mapped = statusMapping[statusId].map((index) => orderStatusDelivery[index][2]);
      setStatusDisable(mapped);
    } else {
      // If not a mapped key, fallback to empty
      setStatusDisable([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusId, orderStatusDelivery, order]);

  // ------------------------------
  // Determine delivery boy location source: Option B (order.deliveryBoyLocation) else Option C (device)
  // ------------------------------
  useEffect(() => {
    if (!order) return;

    // Option B
    if (order.deliveryBoyLocation && order.deliveryBoyLocation.latitude && order.deliveryBoyLocation.longitude) {
      setDeliveryLocation({
        lat: order.deliveryBoyLocation.latitude,
        lng: order.deliveryBoyLocation.longitude,
      });
      // Also optionally keep redux center in sync
      dispatch(setCenter({
        lat: order.deliveryBoyLocation.latitude,
        lng: order.deliveryBoyLocation.longitude,
      }));
    } else {
      // Option C - fallback to device geolocation
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setDeliveryLocation({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            });
            dispatch(setCenter({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            }));
          },
          (err) => {
            console.warn("Geolocation fallback failed", err);
          },
          { enableHighAccuracy: true }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  // ------------------------------
  // Auto-update device location every 10s when trip started (Option C behavior)
  // If deliveryLocation initially came from order's deliveryBoyLocation (B) we still support starting trip,
  // but when tripStarted we always attempt to update from navigator.geolocation (device), as you requested.
  // ------------------------------
  useEffect(() => {
    if (!tripStarted) return;

    let interval = null;

    const updatePos = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const newLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setDeliveryLocation(newLoc);
          dispatch(setCenter(newLoc));
        },
        (err) => {
          console.warn("Unable to fetch live location:", err);
        },
        { enableHighAccuracy: true }
      );
    };

    // update immediately once, then interval
    updatePos();
    interval = setInterval(updatePos, 10000);

    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripStarted]);

  // ------------------------------
  // Helper: set current center from device (used by your previous code)
  // ------------------------------
  const handleSetCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newCenter = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        dispatch(setCenter(newCenter));
        setDeliveryLocation(newCenter);
        toast.success("Location updated successfully");
      },
      (err) => {
        toast.error("Unable to fetch your current location");
      },
      { enableHighAccuracy: true }
    );
  };

  // ------------------------------
  // Calculate Distance & ETA using DistanceMatrixService (requires google maps loaded)
  // ------------------------------
  const calculateDistanceAndTime = useCallback(() => {
    if (!deliveryLocation || !userLocation || !window.google) return;

    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [deliveryLocation],
        destinations: [userLocation],
        travelMode: "DRIVING",
      },
      (response, statusResp) => {
        try {
          const elem = response.rows?.[0]?.elements?.[0];
          if (elem && elem.status === "OK") {
            setDistance(elem.distance?.text || null);
            setEta(elem.duration?.text || null);
          } else {
            setDistance(null);
            setEta(null);
          }
        } catch (e) {
          console.warn("DistanceMatrix error", e);
        }
      }
    );
  }, [deliveryLocation, userLocation]);

  useEffect(() => {
    if (window.google && deliveryLocation && userLocation) calculateDistanceAndTime();
  }, [deliveryLocation, userLocation, calculateDistanceAndTime]);

  // ------------------------------
  // Fetch route for map preview (DirectionsService)
  // ------------------------------
  const fetchRoute = useCallback(() => {
    if (!deliveryLocation || !userLocation || !window.google) return;

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: deliveryLocation,
        destination: userLocation,
        travelMode: "DRIVING",
      },
      (result, statusResp) => {
        if (statusResp === "OK") {
          setDirections(result);
        } else {
          setDirections(null);
        }
      }
    );
  }, [deliveryLocation, userLocation]);

  useEffect(() => {
    if (window.google && deliveryLocation && userLocation) fetchRoute();
  }, [deliveryLocation, userLocation, fetchRoute]);

  // ------------------------------
  // Open Google Maps Directions (external)
  // ------------------------------
  const openGoogleMapsDirection = () => {
    if (!deliveryLocation || !userLocation) {
      toast.error("Location not available!");
      return;
    }
    const origin = `${deliveryLocation.lat},${deliveryLocation.lng}`;
    const destination = `${userLocation.lat},${userLocation.lng}`;
    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
      origin
    )}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
    window.open(url, "_blank");
  };

  // ------------------------------
  // WhatsApp + Call actions
  // ------------------------------
  const sendWhatsApp = () => {
    const phone = order?.user?.phoneNumber || order?.user?.phone;
    if (!phone) return toast.error("Customer phone not found");
    const message = `Hello! I'm on the way to deliver your order. ETA: ${eta || "N/A"}`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const callCustomer = () => {
    const phone = order?.user?.phoneNumber || order?.user?.phone;
    if (!phone) return toast.error("Phone not available");
    window.location.href = `tel:${phone}`;
  };

  // ------------------------------
  // handleStatusChange (preserve your previous behavior)
  // ------------------------------
  const handleStatusChange = async () => {
    try {
      // Keep the same endpoint as your previous code
      await axios.put(
        `${server}/order/update-order-status-delivery/${orderId}`,
        { status, statusId },
        { withCredentials: true }
      );
      toast.success("Order status updated successfully");

      // Your previous code navigated to /all-orders when certain statuses changed.
      // Re-apply: if statusId === orderStatusDelivery[9][2] navigate away (you had a double navigate earlier).
      if (String(statusId) === String(orderStatusDelivery?.[9]?.[2])) {
        navigate(`/order/${orderId}`);
        return;
      }
      // In original code, they navigated regardless; keep that behavior:
      navigate(`/order/${orderId}`);
    } catch (error) {
      console.error("Failed to update order status", error);
      toast.error("Failed to update order status");
    }
  };

  // ------------------------------
  // Render guards
  // ------------------------------
  if (!order) return <div className="p-8">Order not found</div>;

  // ------------------------------
  // Main JSX
  // ------------------------------
  return (
    <div className="w-full min-h-screen p-8 bg-gray-100">
      <Toaster position="bottom-center" />
      <Link
        to="/all-orders"
        className="flex items-center text-cyan-600 hover:text-cyan-800 mb-6"
      >
        <MdArrowBack className="mr-2" />
        Back to All Orders
      </Link>

      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="p-6 flex-1">
          <OrderInfo order={order} handleStatusChange={handleStatusChange} />
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <ShippingInfo order={order} />
          </div>
          <OrderItems order={order} />
        </div>

        {/* status & update row (keeps your original UI) */}
        <div className="p-6 border-t">
          <span
            className={`px-3 py-1 rounded-full text-sm font-semibold ${statusColors[order.status] ||
              "bg-gray-100 text-gray-800"} mr-4`}
          >
            {order.status}
          </span>

          <div className="flex flex-col md:flex-row items-center mt-4 gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-500 mr-2">
                Update Order Status:
              </label>
            </div>

            <div className="flex-1">
              <select
                id={`status-${order._id}`}
                className="form-select text-sm w-full max-w-[260px] border h-[35px] rounded-[5px] bg-cyan-100 border-gray-300 focus:ring-cyan-500 focus:border-cyan-500"
                value={statusId}
                onChange={(e) => {
                  const selected = statusOptions.find(
                    (opt) => String(opt.statusId) === String(e.target.value)
                  );
                  if (selected) {
                    setStatus(selected.status);
                    setStatusId(selected.statusId);
                    setDisplayStatus(selected.displayStatus);
                  } else {
                    setStatusId(e.target.value);
                  }
                }}
              >
                {statusOptions.map(({ statusId: sid, displayStatus }) => (
                  <option
                    value={sid}
                    key={sid}
                    disabled={statusDisable.includes(sid)}
                  >
                    {displayStatus}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <button
                onClick={handleStatusChange}
                className="w-full bg-red-100 text-red-600 px-6 py-2 rounded-lg font-semibold hover:bg-red-500 hover:text-white transition"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Tools */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Delivery Tools</h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <button
            className="col-span-1 md:col-span-1 bg-blue-600 text-white py-2 rounded-lg"
            onClick={() => {
              setTripStarted(true);
              toast.success("Trip started - live tracking enabled");
            }}
          >
            Start Trip
          </button>

          <button
            className="col-span-1 md:col-span-1 bg-green-600 text-white py-2 rounded-lg"
            onClick={openGoogleMapsDirection}
          >
            Navigate in Google Maps
          </button>

          <button
            className="col-span-1 md:col-span-1 bg-green-500 text-white py-2 rounded-lg"
            onClick={sendWhatsApp}
          >
            WhatsApp Customer
          </button>

          <button
            className="col-span-1 md:col-span-1 bg-yellow-500 text-white py-2 rounded-lg"
            onClick={callCustomer}
          >
            Call Customer
          </button>
        </div>

        {/* small controls */}
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            onClick={handleSetCurrentLocation}
            className="px-4 py-2 bg-gray-200 rounded-md"
          >
            Update From Device
          </button>
          <button
            onClick={() => {
              // manual refresh route/distance
              if (window.google && deliveryLocation && userLocation) {
                fetchRoute();
                calculateDistanceAndTime();
                toast.success("Route & ETA refreshed");
              } else {
                toast.error("Locations or Google API not ready");
              }
            }}
            className="px-4 py-2 bg-gray-200 rounded-md"
          >
            Refresh Route & ETA
          </button>
        </div>

        {/* distance / ETA */}
        {distance || eta ? (
          <div className="mt-4 p-3 bg-gray-50 rounded-md">
            <div className="text-sm text-gray-700">
              <strong>Distance:</strong> {distance || "—"}
            </div>
            <div className="text-sm text-gray-700">
              <strong>ETA:</strong> {eta || "—"}
            </div>
          </div>
        ) : null}
      </div>

      {/* Map preview */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div style={{ width: "100%", height: "480px" }}>
          <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={deliveryLocation || reduxCenter || (userLocation || { lat: 20.5937, lng: 78.9629 })}
              zoom={13}
            >
              {deliveryLocation && (
                <Marker label="You" position={deliveryLocation} />
              )}
              {userLocation && (
                <Marker label="Customer" position={userLocation} />
              )}

              {directions && <DirectionsRenderer directions={directions} />}
            </GoogleMap>
          </LoadScript>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
